<template>
  <button
    type="button"
    class="nav-item"
    :class="{ 'nav-item--active': active }"
    @click="emit('select', page.id)"
  >
    <span v-if="active" class="nav-item__accent" aria-hidden="true" />
    <i :class="['pi', `pi-${page.icon}`, 'nav-item__icon']" aria-hidden="true" />
    <span class="nav-item__label">{{ page.label }}</span>
  </button>
</template>

<script setup>
// A single sidebar nav row. Presentational: emits `select` with the page id;
// active state comes from the parent (driven by `activePage`). The icon is a
// PrimeIcon named by `page.icon`.
//
// vibe-portal's copy branched three more ways, and none of them survived the
// nav that came with this app. `page.live` and `page.icon === 'handshake'`
// reached for two hand-drawn glyphs no page here asks for; `page.disabled` drew
// a greyed row with a "Coming soon" title, which is the menu of dead ends this
// app deliberately does not have; `page.iconColor` was never set by anything.
defineProps({
  // `{ id, label, icon }` — a page from a nav section.
  page: { type: Object, required: true },
  active: { type: Boolean, default: false },
})
const emit = defineEmits(['select'])
</script>

<style scoped>
.nav-item {
  position: relative;
  width: 100%;
  height: 36px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  flex-shrink: 0;
  background: transparent;
  color: var(--p-text-muted-color);
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  transition: background 100ms, color 100ms;
}

.nav-item:hover {
  background: var(--p-content-hover-background);
}

.nav-item--active {
  background: var(--p-highlight-background);
  color: var(--p-primary-600);
  font-weight: 700;
}

.nav-item__accent {
  position: absolute;
  left: 0;
  top: 5px;
  bottom: 5px;
  width: 3px;
  border-radius: 999px;
  background: var(--p-primary-500);
}

.nav-item__icon {
  font-size: 15px;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
  opacity: 0.65;
}

.nav-item--active .nav-item__icon {
  opacity: 1;
}

.nav-item__label {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
